import './AddNewPackage.page.css';
import { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../ui/components/LoadingSpinner';
import SidePanelScaffold from '../../ui/pageScaffolds/SidePanelScaffold';
import AdminSideBarContent from '../../ui/panels/SideBars/AdminSideBarContent';
import MainLogo from '../../ui/components/MainLogo';
import InputField from '../../ui/components/InputField';
import DropDownMenu from '../../ui/components/DropDownMenu';
import PageBlocker from '../../ui/components/PageBlocker';
import { LoginState } from '../../types/LoginState';
import BugButton from '../../ui/components/BugButton/BugButton';
import FeedBackButton from '../../ui/components/FeedBackButton/FeedBackButton';

interface PackageFormData {
  packageName: string;
  packageNameError: string;
  packageType: string;
  packageTypeError: string;
  packageDescription: string;
  packageDescriptionError: string;
  selectedEntityId: string | null;
  entityError: string;
  refreshPeriod: string;
  refreshPeriodError: string;
  packagePrice: string;
  packagePriceError: string;
  packageCurrency: string;
  packageCurrencyError: string;
  formError: string;
}

interface EntityInfo {
  entityId: string;
  entityName: string;
}

interface EntityState {
  entityInfo: EntityInfo[];
  selectedEntity: EntityInfo | null;
  loadingEntity: boolean;
  loadingError: boolean;
}

function AddNewPackagePage(): JSX.Element {
  const token = localStorage.getItem('token');
  const nav = useNavigate();

  const refreshPeriodOptions = ['monthly', 'yearly', 'one_time'];
  const currencyOptions = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
  const packageTypeOptions = [
    { display: 'Subscription', value: 'subscription' },
    { display: 'Single Purchase', value: 'one_time' },
  ];

  const [packageFormData, setPackageFormData] = useState<PackageFormData>({
    packageName: '',
    packageNameError: '',
    packageType: 'subscription',
    packageTypeError: '',
    packageDescription: '',
    packageDescriptionError: '',
    selectedEntityId: null,
    entityError: '',
    refreshPeriod: refreshPeriodOptions[0],
    refreshPeriodError: '',
    packagePrice: '',
    packagePriceError: '',
    packageCurrency: currencyOptions[0],
    packageCurrencyError: '',
    formError: '',
  });

  const [entityState, setEntityState] = useState<EntityState>({
    entityInfo: [],
    selectedEntity: null,
    loadingEntity: false,
    loadingError: false,
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [loginState, setLoginState] = useState<LoginState>({
    isLoggedIn: false,
    isAdmin: false,
  });

  const validateUserLogin = async () => {
    if (token) {
      const url = '/validate';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        localStorage.removeItem('token');
        nav('/');
        return;
      }
      const json = await response.json();

      if (json.valid && json.valid === true) {
        setLoginState({ isLoggedIn: true, isAdmin: true });
      } else {
        localStorage.removeItem('token');
        nav('/');
      }
    }
  };

  const requestEntityData = async () => {
    const url = `/admin/entities`;
    try {
      setEntityState({
        ...entityState,
        loadingEntity: true,
      });
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          nav('/signin');
        }
        throw new Error('Network response was not ok ' + response.statusText);
      }
      const jsonResponse = await response.json();
      const info: EntityInfo[] = JSON.parse(jsonResponse) as EntityInfo[];

      if (info.length === 0) {
        console.log(`Are you an admin?`);
        throw Error(`Admin is admin of 0 entities`);
      }

      setEntityState({
        entityInfo: info,
        selectedEntity: info[0],
        loadingEntity: false,
        loadingError: false,
      });

      // Set the first entity as selected by default
      setPackageFormData((prev) => ({
        ...prev,
        selectedEntityId: info[0].entityId,
      }));
    } catch (error) {
      setEntityState({
        ...entityState,
        loadingEntity: false,
        loadingError: true,
      });
      console.log(`An error occurred when trying to get entity data`);
      console.log(error);
    }
  };

  useEffect(() => {
    validateUserLogin();
  }, []);

  useEffect(() => {
    if (
      loginState.isLoggedIn &&
      entityState.entityInfo.length === 0 &&
      !entityState.loadingEntity
    ) {
      requestEntityData();
    }
  }, [loginState.isLoggedIn]);

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const validatePrice = (price: string): boolean => {
    const priceNum = parseFloat(price);
    return !isNaN(priceNum) && priceNum >= 0;
  };

  const handlePackageNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPackageFormData({
      ...packageFormData,
      packageName: event.target.value,
      packageNameError:
        event.target.value.length < 3 && event.target.value !== ''
          ? 'Package name is too short (minimum 3 characters)'
          : '',
      formError: '',
    });
  };

  const handlePackageTypeSelected = (displayValue: string) => {
    const selectedOption = packageTypeOptions.find(
      (opt) => opt.display === displayValue
    );
    if (selectedOption) {
      setPackageFormData({
        ...packageFormData,
        packageType: selectedOption.value,
        packageTypeError: '',
        formError: '',
      });
    }
  };

  const handlePackageDescriptionChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setPackageFormData({
      ...packageFormData,
      packageDescription: event.target.value,
      packageDescriptionError: '',
      formError: '',
    });
  };

  const handleEntitySelected = (entityName: string) => {
    const entity = entityState.entityInfo.find(
      (e) => e.entityName === entityName
    );
    if (entity) {
      setEntityState({
        ...entityState,
        selectedEntity: entity,
      });
      setPackageFormData({
        ...packageFormData,
        selectedEntityId: entity.entityId,
        entityError: '',
        formError: '',
      });
    }
  };

  const handleRefreshPeriodSelected = (period: string) => {
    setPackageFormData({
      ...packageFormData,
      refreshPeriod: period,
      refreshPeriodError: '',
      formError: '',
    });
  };

  const handlePackagePriceChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPackageFormData({
      ...packageFormData,
      packagePrice: event.target.value,
      packagePriceError:
        !validatePrice(event.target.value) && event.target.value !== ''
          ? 'Invalid price format'
          : '',
      formError: '',
    });
  };

  const handleCurrencySelected = (currency: string) => {
    setPackageFormData({
      ...packageFormData,
      packageCurrency: currency,
      packageCurrencyError: '',
      formError: '',
    });
  };

  const handleFormClear = () => {
    setPackageFormData({
      packageName: '',
      packageNameError: '',
      packageType: 'subscription',
      packageTypeError: '',
      packageDescription: '',
      packageDescriptionError: '',
      selectedEntityId: entityState.selectedEntity?.entityId || null,
      entityError: '',
      refreshPeriod: refreshPeriodOptions[0],
      refreshPeriodError: '',
      packagePrice: '',
      packagePriceError: '',
      packageCurrency: currencyOptions[0],
      packageCurrencyError: '',
      formError: '',
    });
  };

  const handlePackageCreation = async () => {
    // Validate all fields
    if (packageFormData.packageNameError !== '') return;
    if (packageFormData.packagePriceError !== '') return;

    if (packageFormData.packageName === '') {
      setPackageFormData({
        ...packageFormData,
        packageNameError: 'Package name is required',
      });
      return;
    }

    if (packageFormData.selectedEntityId === null) {
      setPackageFormData({
        ...packageFormData,
        entityError: 'Please select an entity',
      });
      return;
    }

    if (packageFormData.packagePrice === '') {
      setPackageFormData({
        ...packageFormData,
        packagePriceError: 'Package price is required',
      });
      return;
    }

    const priceNum = parseFloat(packageFormData.packagePrice);
    if (isNaN(priceNum)) {
      setPackageFormData({
        ...packageFormData,
        packagePriceError: 'Invalid price format',
      });
      return;
    }

    const jsonData = JSON.stringify({
      packageName: packageFormData.packageName,
      packageType: packageFormData.packageType,
      packageDescription: packageFormData.packageDescription || undefined,
      entityId: packageFormData.selectedEntityId,
      refreshPeriod: packageFormData.refreshPeriod,
      packagePrice: priceNum,
      packageCurrency: packageFormData.packageCurrency,
    });

    const url = '/admin/package/add';
    setLoading(true);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: jsonData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          nav('/signin');
        } else if (response.status === 400) {
          throw new Error('Invalid request. Please check your input.');
        } else {
          throw new Error('Failed to create package. Please try again later.');
        }
      }

      handleFormClear();
    } catch (error: any) {
      if (error.message) {
        setPackageFormData({
          ...packageFormData,
          formError: error.message,
        });
      }
    }

    setLoading(false);
  };

  let mainPageContent: JSX.Element;

  if (entityState.loadingEntity) {
    mainPageContent = <LoadingSpinner />;
  } else {
    mainPageContent = (
      <div className="AddNewPackagePage">
        <h2 className="AddNewPackageTitle">Create New Package</h2>
        <div className="AddNewPackageForm">
          {/* Entity Selection */}
          <div className="AddNewPackageFormRow">
            <span className="AddNewPackageLabel">Entity *</span>
          </div>
          <div className="AddNewPackageFormRow">
            <DropDownMenu
              choices={entityState.entityInfo.map((item) => item.entityName)}
              onSelect={handleEntitySelected}
            />
          </div>
          {packageFormData.entityError !== '' && (
            <div className="AddNewPackageFormRow">
              <span className="AddNewPackageError">
                {packageFormData.entityError}
              </span>
            </div>
          )}

          {/* Package Name */}
          <div className="AddNewPackageFormRow">
            <span className="AddNewPackageLabel">Package Name *</span>
          </div>
          <div className="AddNewPackageFormRow">
            <InputField
              value={packageFormData.packageName}
              label={'Package Name'}
              onValueChange={handlePackageNameChange}
            />
          </div>
          {packageFormData.packageNameError !== '' && (
            <div className="AddNewPackageFormRow">
              <span className="AddNewPackageError">
                {packageFormData.packageNameError}
              </span>
            </div>
          )}

          {/* Package Type */}
          <div className="AddNewPackageFormRow">
            <span className="AddNewPackageLabel">Package Type *</span>
          </div>
          <div className="AddNewPackageFormRow">
            <DropDownMenu
              choices={packageTypeOptions.map((option) => option.display)}
              onSelect={handlePackageTypeSelected}
            />
          </div>

          {/* Package Description */}
          <div className="AddNewPackageFormRow">
            <span className="AddNewPackageLabel">
              Package Description (Optional)
            </span>
          </div>
          <div className="AddNewPackageFormRow">
            <InputField
              value={packageFormData.packageDescription}
              label={'Package Description'}
              onValueChange={handlePackageDescriptionChange}
            />
          </div>

          {/* Refresh Period */}
          <div className="AddNewPackageFormRow">
            <span className="AddNewPackageLabel">Refresh Period *</span>
          </div>
          <div className="AddNewPackageFormRow">
            <DropDownMenu
              choices={refreshPeriodOptions}
              onSelect={handleRefreshPeriodSelected}
            />
          </div>

          {/* Package Price */}
          <div className="AddNewPackageFormRow">
            <span className="AddNewPackageLabel">Package Price *</span>
          </div>
          <div className="AddNewPackageFormRow">
            <InputField
              value={packageFormData.packagePrice}
              label={'Package Price'}
              onValueChange={handlePackagePriceChange}
            />
          </div>
          {packageFormData.packagePriceError !== '' && (
            <div className="AddNewPackageFormRow">
              <span className="AddNewPackageError">
                {packageFormData.packagePriceError}
              </span>
            </div>
          )}

          {/* Currency */}
          <div className="AddNewPackageFormRow">
            <span className="AddNewPackageLabel">Currency *</span>
          </div>
          <div className="AddNewPackageFormRow">
            <DropDownMenu
              choices={currencyOptions}
              onSelect={handleCurrencySelected}
            />
          </div>

          {/* Form Error */}
          {packageFormData.formError !== '' && (
            <div className="AddNewPackageFormRow">
              <span className="AddNewPackageError">
                {packageFormData.formError}
              </span>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="AddNewPackageFormSubmitRow">
            <button
              className="AddNewPackageFormButton"
              onClick={handleFormClear}
            >
              Clear
            </button>
            <button
              className="AddNewPackageFormButton AddNewPackageFormButtonPrimary"
              onClick={handlePackageCreation}
            >
              Create Package
            </button>
          </div>
        </div>

        {loading && (
          <PageBlocker
            isDismissable={false}
            onDismiss={() => {}}
            children={<LoadingSpinner />}
          />
        )}
      </div>
    );
  }

  return (
    <SidePanelScaffold
      isLoggedIn={loginState.isLoggedIn}
      isAdmin={loginState.isAdmin}
      stickyTopBar={true}
      isExpanded={isExpanded}
      showCollapseIcons={true}
      onExpandedToggle={handleExpandToggle}
      mainPageChildren={
        <>
          {mainPageContent}
          {loginState.isLoggedIn && (
            <>
              <BugButton
                parentPageUrl={'/admin/package/add'}
                token={token}
                nav={nav}
              />
              <FeedBackButton
                parentPageUrl={'/admin/package/add'}
                token={token}
                nav={nav}
              />
            </>
          )}
        </>
      }
      sideBarChildren={<AdminSideBarContent nav={nav} />}
      topBarChildren={
        <>
          <MainLogo nav={nav} />
          <div />
        </>
      }
    />
  );
}

export default AddNewPackagePage;
